import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateFeed,
  getFeedStatus,
  regenerateFeed,
  deactivateFeed,
} from '../services/api';
import type { FeedMetadata, ActivateFeedResponse } from '../types';

/**
 * T101 [US2]: Feed hooks — TanStack React Query wrappers.
 *
 * @see contracts/feed.md
 * @module
 */

/** Fetch current feed status and metadata. Polls every 5s while GENERATING. */
export function useFeedStatus() {
  const query = useQuery<FeedMetadata>({
    queryKey: ['feed-status'],
    queryFn: () => getFeedStatus(),
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'GENERATING' ? 5_000 : false;
    },
  });

  return query;
}

/** Activate a store's product feed. Invalidates feed status on success. */
export function useActivateFeed() {
  const queryClient = useQueryClient();

  return useMutation<ActivateFeedResponse, Error>({
    mutationFn: () => activateFeed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed-status'] });
    },
  });
}

/** Regenerate the feed. Invalidates feed status on success. */
export function useRegenerateFeed() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => regenerateFeed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed-status'] });
    },
  });
}

/** Deactivate the feed. Invalidates feed status on success. */
export function useDeactivateFeed() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => deactivateFeed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed-status'] });
    },
  });
}
